import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Helmet } from "react-helmet";
import Grid from "@mui/material/Grid2";
import { motion } from "framer-motion";
import Footer from "../Footer";
import BrandMemoryGame from "./BrandMemoryGame";
import AboutUs from "../About";
import ThisOrThat from "../Games/ThisOrThat";
import { ThemeProvider } from "@mui/material/styles";
import brandTheme from "./brandTheme";

// Sample photos for the memory game preview
const samplePhotos = [
  "/assets/brand-page/memory-sample/photo1.jpeg",
  "/assets/brand-page/memory-sample/photo2.jpeg",
  "/assets/brand-page/memory-sample/photo3.jpeg",
  "/assets/brand-page/memory-sample/photo4.jpeg",
  "/assets/brand-page/memory-sample/photo5.jpeg",
  "/assets/brand-page/memory-sample/photo6.jpeg",
  "/assets/brand-page/memory-sample/photo7.jpg",
  "/assets/brand-page/memory-sample/photo8.jpg",
];

// Custom pairs for the hero demo
// Custom pairs for the hero demo (PopCrave/Betches vibe)
const HERO_PAIRS = [
  { left: "🫦 Ice Spice", right: "💅 Olivia Rodrigo" },
  { left: "🛍 Hot Girl Walk", right: "📉 Soft Launch Spiral" },
  { left: "🪩 Rave Night", right: "🍸 Girls' Night In" },
  { left: "💄 Lip Gloss", right: "🖤 Lip Liner" },
  { left: "📺 Love Island", right: "💔 The Bachelor" },
  { left: "🎤 Beyoncé", right: "🎤 Taylor Swift" },
  { left: "🦋 Coquette Core", right: "🔥 Brat Core" },
  { left: "📱 TikTok Deep Dive", right: "🧠 Reddit Black Hole" },
  { left: "🛏 Sleeping In", right: "⏰ 6am Pilates" },
  { left: "🥂 Espresso Martini", right: "🍸 Dirty Martini" },
];

const MemoryGamePreview = () => (
  <Box borderRadius={"12px"} border={"1px solid #E0E0E0"} overflow={"hidden"}>
    <BrandMemoryGame
      photos={samplePhotos}
      title="Memory Match"
      hideTitle={false}
      logoPath="/assets/logo.svg"
    />
  </Box>
);

const PopQuizPreview = () => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const correctAnswer = "A$AP Rocky";
  const answers = ["A$AP Rocky", "Taylor Swift", "Zendaya", "Billie Eilish"];

  // For confetti emoji burst
  const [showConfetti, setShowConfetti] = useState(false);
  // For shake animation on wrong answer
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (selectedAnswer && selectedAnswer === correctAnswer) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 1200);
      return () => clearTimeout(timer);
    } else if (selectedAnswer && selectedAnswer !== correctAnswer) {
      setShake(true);
      const timer = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [selectedAnswer]);

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #A78BFA 0%, #6366F1 100%)",
        borderRadius: "24px",
        p: 4,
        color: "white",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          color: "white",
        }}
      >
        <span role="img" aria-label="sparkle">
          🧠
        </span>{" "}
        Pop Culture IQ
      </Typography>
      <Box
        sx={{
          backgroundColor: "rgba(255,255,255,0.2)",
          borderRadius: "16px",
          p: 3,
          mb: 2,
        }}
      >
        Which artist is co-hosting the 2025 Met Gala alongside Anna Wintour?
      </Box>
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
        {answers.map((answer) => {
          const isSelected = selectedAnswer === answer;
          const isCorrect = answer === correctAnswer;
          const isWrong = isSelected && !isCorrect;
          return (
            <Grid item xs={12} sm={6} md={3} key={answer}>
              <Box
                onClick={() =>
                  selectedAnswer === null && setSelectedAnswer(answer)
                }
                sx={{
                  backgroundColor: isSelected
                    ? isCorrect
                      ? "#e6faee"
                      : "#faeaea"
                    : selectedAnswer && isCorrect && answer === correctAnswer
                    ? "#e6faee"
                    : "rgba(255,255,255,0.12)",
                  borderRadius: "16px",
                  p: 2.5,
                  cursor: selectedAnswer ? "default" : "pointer",
                  transition: "all 0.18s cubic-bezier(.68,-0.55,.27,1.55)",
                  position: "relative",
                  border: isSelected
                    ? isCorrect
                      ? "2px solid #22c55e"
                      : "2px solid #ef4444"
                    : isCorrect && selectedAnswer
                    ? "2px solid #22c55e"
                    : "2px solid transparent",
                  color: isSelected
                    ? isCorrect
                      ? "#16a34a"
                      : "#dc2626"
                    : selectedAnswer && isCorrect && answer === correctAnswer
                    ? "#16a34a"
                    : selectedAnswer
                    ? "#bbb"
                    : "white",
                  opacity:
                    selectedAnswer &&
                    !isSelected &&
                    !(isCorrect && selectedAnswer)
                      ? 0.5
                      : 1,
                  fontWeight:
                    isSelected || (isCorrect && selectedAnswer) ? 700 : 400,
                  boxShadow:
                    isSelected || (isCorrect && selectedAnswer)
                      ? "0 4px 16px rgba(0,0,0,0.10)"
                      : undefined,
                  transform:
                    isSelected && isCorrect
                      ? "scale(1.07)"
                      : isCorrect && selectedAnswer
                      ? "scale(1.04)"
                      : isWrong && shake
                      ? "scale(1.02) translateX(-4px)"
                      : "scale(1)",
                  zIndex: isSelected ? 2 : 1,
                  outline: isWrong && shake ? "2px solid #ef4444" : undefined,
                  animation: isWrong && shake ? "shake 0.4s" : undefined,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 56,
                  fontSize: "1.1rem",
                  userSelect: "none",
                }}
              >
                {isCorrect && selectedAnswer ? (
                  <span style={{ fontSize: "1.2em", marginRight: 6 }}>✅</span>
                ) : null}
                {isWrong ? (
                  <span style={{ fontSize: "1.2em", marginRight: 6 }}>❌</span>
                ) : null}
                {answer}
                {/* Confetti emoji burst for correct answer */}
                {isSelected && isCorrect && showConfetti && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "-30px",
                      transform: "translateX(-50%)",
                      fontSize: "2rem",
                      pointerEvents: "none",
                      userSelect: "none",
                      animation: "popConfetti 0.8s",
                    }}
                  >
                    🎉🎊✨
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      {/* Feedback below the answers */}
      <Box
        sx={{
          minHeight: "32px",
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "1rem",
          color: selectedAnswer
            ? selectedAnswer === correctAnswer
              ? "#16a34a"
              : "#dc2626"
            : "#fff",
          opacity: selectedAnswer ? 1 : 0.7,
          transition: "all 0.3s",
          letterSpacing: 0.2,
          background: selectedAnswer
            ? selectedAnswer === correctAnswer
              ? "#e6faee"
              : "#faeaea"
            : "rgba(0,0,0,0.00)",
          borderRadius: "12px",
          px: 3,
          py: 1,
          boxShadow: selectedAnswer ? "0 2px 8px rgba(0,0,0,0.04)" : undefined,
        }}
      >
        {selectedAnswer ? (
          selectedAnswer === correctAnswer ? (
            <>
              <span style={{ fontSize: "1.2em", marginRight: 6 }}>🎉</span>
              You got it! A$AP Rocky is co-hosting the 2025 Met Gala.
            </>
          ) : (
            <>
              <span style={{ fontSize: "1.2em", marginRight: 6 }}>😬</span>
              Not quite! Check the news for more pop culture updates.
            </>
          )
        ) : (
          "Tap an answer above to test your pop culture IQ!"
        )}
      </Box>
      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translate(-50%, 30px); }
          to { opacity: 1; transform: translate(-50%, 10px); }
        }
        @keyframes popConfetti {
          0% { opacity: 0; transform: translateX(-50%) scale(0.7); }
          60% { opacity: 1; transform: translateX(-50%) scale(1.2); }
          100% { opacity: 0; transform: translateX(-50%) scale(1); }
        }
        @keyframes shake {
          0% { transform: scale(1.02) translateX(0); }
          20% { transform: scale(1.02) translateX(-6px); }
          40% { transform: scale(1.02) translateX(6px); }
          60% { transform: scale(1.02) translateX(-4px); }
          80% { transform: scale(1.02) translateX(4px); }
          100% { transform: scale(1.02) translateX(0); }
        }
      `}</style>
    </Box>
  );
};

const BrandGamePreviews = {
  "pop-quiz": PopQuizPreview,
  "memory-game": MemoryGamePreview,
  "this-or-that": ThisOrThat,
};

const BrandHome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedPreview, setSelectedPreview] = useState("this-or-that");

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <ThemeProvider theme={brandTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          backgroundColor: "white",
        }}
      >
        <Helmet>
          <title>Flying Comet Games | Custom Brand Games That Go Viral</title>
          <meta
            name="description"
            content="Turn your brand's content into addictive daily games. Launch in 7 days, drive engagement, and create viral moments."
          />
        </Helmet>

        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #6366F1 0%, #FF61D8 100%)",
            color: "white",
            pt: { xs: 8, md: 12 },
            pb: { xs: 12, md: 16 },
            position: "relative",
          }}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                maxWidth: { xs: "100%", md: "1200px" },
                mx: "auto",
                px: { xs: 2, md: 4 },
                position: "relative",
                zIndex: 2,
              }}
            >
              <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                <Grid item size={{ xs: 12, md: 7 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "2.2rem", md: "3.75rem" },
                      fontWeight: 800,
                      mb: 1,
                      lineHeight: 1.1,
                      color: "white",
                    }}
                  >
                    Turn your content into addictive games
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      fontWeight: 400,
                      mb: 4,
                      opacity: 0.9,
                      color: "white",
                      maxWidth: "600px",
                    }}
                  >
                    Launch custom, brand-native games that keep your audience
                    coming back daily. Ready in 7 days.
                  </Typography>
                  <Button
                    variant="contained"
                    href="https://cal.com/calli-fuchigami/15min"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "white",
                      color: "#000",
                      px: 6,
                      py: 3,
                      borderRadius: "100px",
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                      minHeight: "2.5em",
                      mb: { xs: 6, md: 0 },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.95)",
                        color: "#000",
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 20px rgba(0,0,0,0.15)",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Get your custom game mockup
                  </Button>
                </Grid>

                <Grid item size={{ xs: 12, md: 5 }} width={"450px"}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 420,
                        background: "white",
                        borderRadius: "24px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                        p: { xs: 2, md: 3 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 0,
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          width: "100%",
                          mt: 1,
                        }}
                      >
                        <Box sx={{ width: "100%", maxWidth: 380, mx: "auto" }}>
                          <ThisOrThat
                            pairs={HERO_PAIRS}
                            brandColors={{
                              primary: "#FF61D8",
                              secondary: "#6366F1",
                              background: "transparent",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>

          {/* Decorative background elements */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background:
                "radial-gradient(circle at 20% 150%, #FF61D8 0%, transparent 50%)",
            }}
          />
        </Box>

        {/* What We Do Section */}
        <Box
          py={12}
          sx={{
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,97,216,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  fontWeight: 800,
                  mb: 4,
                  textAlign: "center",
                  color: "#A259FF",
                }}
              >
                Not just games.{" "}
                <span style={{ fontStyle: "italic" }}>Brand experiences.</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.1rem", md: "1.25rem" },
                  textAlign: "center",
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 8,
                  color: "text.secondary",
                }}
              >
                We help lifestyle and culture brands turn their content into
                daily habits that audiences love.
              </Typography>

              <Grid
                container
                spacing={4}
                sx={{ mb: 4 }}
                justifyContent="center"
              >
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "#FF61D8",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          background: "#FF61D8",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        🎮 Brand-Native Games
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          lineHeight: 1.6,
                          color: "text.primary",
                        }}
                      >
                        Custom games that feel like your brand — from aesthetic
                        quizzes to pop culture showdowns.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "#6366F1",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          background: "#FF61D8",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        📱 Built for Social
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          lineHeight: 1.6,
                          color: "text.primary",
                        }}
                      >
                        Drive shares, saves, and viral moments with games
                        designed for Instagram, TikTok, and beyond.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "#FFF",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      transition: "all 0.3s ease-in-out",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        borderColor: "#BE4DFF",
                        backgroundColor: "rgba(255,255,255,0.95)",
                      },
                    }}
                  >
                    <Box mb={3}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "1.25rem",
                          fontWeight: 700,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          background: "#FF61D8",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        ⚡ Launch in 7 Days
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          lineHeight: 1.6,
                          color: "text.primary",
                        }}
                      >
                        From concept to launch in one week. Perfect for
                        campaigns, product drops, or daily engagement.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Why Games Work Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#F8F0FF",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,97,216,0.2), transparent)",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Box mb={8} sx={{ textAlign: "center" }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3rem" },
                    fontWeight: 800,
                    mb: 3,
                    color: "#A259FF",
                    position: "relative",
                    display: "inline-block",
                  }}
                >
                  Why Games Work
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    maxWidth: "800px",
                    mx: "auto",
                    mt: 4,
                    color: "text.secondary",
                    lineHeight: 1.8,
                  }}
                >
                  Games aren't just fun — they convert. <br />
                  When your audience plays with your brand, they remember it.
                </Typography>
              </Box>

              <Grid container spacing={4} justifyContent="center">
                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      minHeight: "320px",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                        borderColor: "#FF61D8",
                        "& .icon-wrapper": {
                          transform: "scale(1.1)",
                          background:
                            "linear-gradient(135deg, rgba(255,97,216,0.15) 0%, rgba(255,97,216,0.05) 100%)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="icon-wrapper"
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,97,216,0.1)",
                        mb: 3,
                        transition: "all 0.4s ease",
                      }}
                    >
                      <Typography sx={{ fontSize: "2.5rem" }}>⏱️</Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#333",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-8px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "40px",
                          height: "2px",
                          background: "#FF61D8",
                          borderRadius: "1px",
                        },
                      }}
                    >
                      Daily repeat traffic
                    </Typography>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                        mt: 3,
                        px: 2,
                      }}
                    >
                      Become part of your audience's daily scroll routine
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      minHeight: "320px",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                        borderColor: "#6366F1",
                        "& .icon-wrapper": {
                          transform: "scale(1.1)",
                          background:
                            "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0.05) 100%)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="icon-wrapper"
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(99,102,241,0.1)",
                        mb: 3,
                        transition: "all 0.4s ease",
                      }}
                    >
                      <Typography sx={{ fontSize: "2.5rem" }}>🔁</Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#333",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-8px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "40px",
                          height: "2px",
                          background: "#6366F1",
                          borderRadius: "1px",
                        },
                      }}
                    >
                      Shares and saves
                    </Typography>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                        mt: 3,
                        px: 2,
                      }}
                    >
                      Create viral moments across Instagram, TikTok, and more
                    </Typography>
                  </Box>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      minHeight: "320px",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                        borderColor: "#BE4DFF",
                        "& .icon-wrapper": {
                          transform: "scale(1.1)",
                          background:
                            "linear-gradient(135deg, rgba(190,77,255,0.15) 0%, rgba(190,77,255,0.05) 100%)",
                        },
                      },
                    }}
                  >
                    <Box
                      className="icon-wrapper"
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(190,77,255,0.1)",
                        mb: 3,
                        transition: "all 0.4s ease",
                      }}
                    >
                      <Typography sx={{ fontSize: "2.5rem" }}>💸</Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        mb: 2,
                        color: "#333",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-8px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "40px",
                          height: "2px",
                          background: "#BE4DFF",
                          borderRadius: "1px",
                        },
                      }}
                    >
                      Clicks that convert
                    </Typography>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                        mt: 3,
                        px: 2,
                      }}
                    >
                      Turn engagement into product and affiliate revenue
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Game Preview Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#f8f9fa",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(255,97,216,0.2), transparent)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              height: "50%",
              background:
                "radial-gradient(circle at center, rgba(255,97,216,0.05) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
          id="preview-your-game"
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Box mb={8} sx={{ textAlign: "center" }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "#FF61D8",
                    mb: 2,
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  Interactive Demos
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3rem" },
                    fontWeight: 800,
                    mb: 3,
                    color: "#A259FF",
                    position: "relative",
                    display: "inline-block",
                  }}
                >
                  Preview Your Game
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    maxWidth: "800px",
                    mx: "auto",
                    mt: 4,
                    color: "text.secondary",
                    lineHeight: 1.8,
                  }}
                >
                  See how your brand could engage audiences with custom games
                </Typography>
              </Box>

              <Grid container spacing={4} sx={{ mb: 8 }}>
                {Object.entries(BrandGamePreviews).map(
                  ([key, Component], idx) => (
                    <Grid
                      item
                      size={{ xs: 12, md: idx === 0 ? 12 : 6 }}
                      key={key}
                    >
                      <Box
                        sx={{
                          p: 4,
                          backgroundColor: "white",
                          borderRadius: "24px",
                          border: "1px solid rgba(0,0,0,0.08)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        }}
                      >
                        <Component />
                      </Box>
                    </Grid>
                  )
                )}
              </Grid>

              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "24px",
                  p: { xs: 4, md: 6 },
                  textAlign: "center",
                  border: "1px solid rgba(255,97,216,0.1)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                }}
              >
                <Grid container spacing={4} justifyContent="center">
                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        mb: { xs: 2, md: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(255,97,216,0.1)",
                          color: "#FF61D8",
                        }}
                      >
                        ✓
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          color: "text.primary",
                          fontWeight: 500,
                        }}
                      >
                        Get a mockup of a custom game for your brand
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        mb: { xs: 2, md: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(99,102,241,0.1)",
                          color: "#6366F1",
                        }}
                      >
                        🎨
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          color: "text.primary",
                          fontWeight: 500,
                        }}
                      >
                        Tailored to your aesthetic, voice, and audience
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(190,77,255,0.1)",
                          color: "#BE4DFF",
                        }}
                      >
                        🗓️
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          color: "text.primary",
                          fontWeight: 500,
                        }}
                      >
                        Delivered after a quick 15-min call
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box mt={6}>
                  <Button
                    variant="contained"
                    href="https://cal.com/calli-fuchigami/15min"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background:
                        "linear-gradient(45deg, #FF61D8 30%, #6366F1 90%)",
                      color: "white",
                      px: { xs: 4, md: 6 },
                      py: { xs: 1.5, md: 2 },
                      borderRadius: "100px",
                      fontSize: { xs: "1.1rem", md: "1.2rem" },
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                      minHeight: "2.5em",
                      transition: "all 0.3s ease-in-out",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #6366F1 30%, #FF61D8 90%)",
                        transform: "translateY(-2px) scale(1.05)",
                        boxShadow: "0 12px 20px rgba(255,97,216,0.2)",
                        "&::after": {
                          transform: "translateY(-50%) scale(1.1)",
                          opacity: 0.4,
                        },
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: -20,
                        right: -20,
                        height: "200%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                        transform: "translateY(-50%) scale(1)",
                        opacity: 0,
                        transition: "all 0.6s ease-in-out",
                      },
                    }}
                  >
                    Get your custom game mockup →
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          py={12}
          sx={{
            backgroundColor: "#F8F0FF",
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4} textAlign="center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.75rem", md: "3.5rem" },
                  fontWeight: 800,
                  mb: 3,
                  color: "#A259FF",
                }}
              >
                Ready to play?
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  mb: 6,
                  maxWidth: "600px",
                  mx: "auto",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                Let's create a game that feels like your brand and keeps your
                audience coming back for more.
              </Typography>

              {/* Final CTA with enhanced hover */}
              <Button
                variant="contained"
                href="https://cal.com/calli-fuchigami/15min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  background:
                    "linear-gradient(45deg, #FF61D8 30%, #6366F1 90%)",
                  color: "white",
                  px: { xs: 4, md: 6 },
                  py: { xs: 1.5, md: 2 },
                  borderRadius: "100px",
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  minHeight: "2.5em",
                  transition: "all 0.3s ease-in-out",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #6366F1 30%, #FF61D8 90%)",
                    transform: "translateY(-2px) scale(1.05)",
                    boxShadow: "0 12px 20px rgba(255,97,216,0.2)",
                    "&::after": {
                      transform: "translateY(-50%) scale(1.1)",
                      opacity: 0.4,
                    },
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: -20,
                    right: -20,
                    height: "200%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                    transform: "translateY(-50%) scale(1)",
                    opacity: 0,
                    transition: "all 0.6s ease-in-out",
                  },
                }}
              >
                Get your custom game mockup →
              </Button>
            </motion.div>
          </Box>
        </Box>

        <AboutUs isComponent={true} />

        <Footer bgColor="#F8F0FF" />
      </Box>
    </ThemeProvider>
  );
};

export default BrandHome;
